import React from 'react';
import styled from 'styled-components';

import AlertSVG from '../../assets/icons/alert.svg';
import CheckmarkSVG from '../../assets/icons/checkmark.svg';
import XIconSVG from '../../assets/icons/x-icon.svg';
import { colors } from '../constants';

// Resembles speech bubble with light-gray background color

interface MessageBubbleActionItemProps {
  isPositiveAction: boolean;
  actionText: string;
  id?: string;
}

// Styles -----------------------------------------------------------------------------------------
// When changing the background color, be sure to change the background color of the 'caret' too
// Refer to ErrorMessageBubble below as an example
export const MessageBubble = styled.div`
  position: relative;
  min-height: 2rem;
  max-width: 100%;
  margin: 0.5rem 0 0;
  border-radius: 0.125rem;
  padding: 0.625rem 1rem;
  display: flex;
  flex-direction: column;
  line-height: 1.375rem;
  font-size: 0.875rem;
  color: ${colors.TEXT_INPUT};
  background-color: ${colors.UI_SECONDARY};

  /* Render the 'caret' along the top edge of the speech bubble */
  &::before {
    content: '\\00a0';
    position: absolute;
    top: -0.3125rem;
    left: 1.5rem;
    height: 0.625rem;
    width: 0.625rem;
    margin: 0.0625rem 0 0;
    display: block;
    color: ${colors.TEXT_INPUT};
    background-color: ${colors.UI_SECONDARY};
    transform: rotate(45deg);
  }
`;
MessageBubble.displayName = 'MessageBubble';

// Error message bubble with light-red background color and dark grey text
export const ErrorMessageBubble = styled(MessageBubble)`
  display: flex;
  && {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  min-height: 3rem;
  padding: 0.75rem 1rem;
  line-height: 1.375rem;
  color: ${colors.TEXT_INPUT};
  background-color: ${colors.TEXT_ERROR_ACCENT}; /* when background color is changed here... */

  &::before {
    background-color: ${colors.TEXT_ERROR_ACCENT}; /* ...change background color here too */
  }
`;
ErrorMessageBubble.displayName = 'ErrorMessageBubble';

export const ErrorMessageText = styled.p``;
ErrorMessageText.displayName = 'ErrorMessageText';

export const MessageBubbleActionList = styled.div``;
MessageBubbleActionList.displayName = 'MessageBubbleActionList';

export const MessageBubbleHeaderText = styled.p`
  margin: 0.5rem 0 0;
  font-weight: bold;
  font-size: 0.875rem;
  color: ${colors.TEXT_SECONDARY};
`;
MessageBubbleHeaderText.displayName = 'MessageBubbleHeaderText';

const MessageBubbleActionItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  line-height: 1.5rem;
`;
MessageBubbleActionItemContainer.displayName =
  'MessageBubbleActionItemContainer';

// Icon container at the inner-right edge of the MessageBubble
const MessageBubbleIconContainer = styled.div`
  height: 1.5rem;
  width: 1.5rem;
  display: flex;
  pointer-events: none;
  margin-left: 0.75rem;
`;
MessageBubbleIconContainer.displayName = 'MessageBubbleIconContainer';

const MessageBubbleAlertIcon = styled(AlertSVG)``;
MessageBubbleAlertIcon.displayName = 'MessageBubbleAlertIcon';

const MessageBubbleCheckIcon = styled(CheckmarkSVG)`
  height: 0.75rem;
  width: 0.75rem;
  min-width: 0.75rem;
  margin: 0.3875rem 0.3125rem 0 0;
`;
MessageBubbleCheckIcon.displayName = 'MessageBubbleCheckIcon';

const MessageBubbleXIcon = styled(XIconSVG)`
  height: 0.75rem;
  width: 0.75rem;
  min-width: 0.75rem;
  margin: 0.3125rem 0.3125rem 0 0;
`;
MessageBubbleXIcon.displayName = 'MessageBubbleXIcon';

// Component --------------------------------------------------------------------------------------
export const MessageBubbleActionItem = (
  props: MessageBubbleActionItemProps
) => {
  const { id, isPositiveAction, actionText } = props;
  return (
    <MessageBubbleActionItemContainer id={id} role="listitem">
      {isPositiveAction ? <MessageBubbleCheckIcon /> : <MessageBubbleXIcon />}
      <p>{actionText}</p>
    </MessageBubbleActionItemContainer>
  );
};

export const ErrorMessageBubbleIcon = () => (
  <MessageBubbleIconContainer>
    <MessageBubbleAlertIcon />
  </MessageBubbleIconContainer>
);

MessageBubbleActionItem.defaultProps = {
  id: undefined,
};
